import { ReactRenderer } from "@tiptap/react";
import { SuggestionOptions, SuggestionProps } from "@tiptap/suggestion";

import DotPhraseSuggestionList, {
  DotPhraseSuggestionListProps,
} from "../components/DotPhraseSuggestionList";
import { InsertableDotPhrase } from "../types/insertable-dot-phrase";

import classes from "./dot-phrase-suggestion-renderer.module.css";

const POPUP_GAP = 4;

type SuggestionRender = NonNullable<SuggestionOptions<InsertableDotPhrase>["render"]>;
type PhraseRenderer = ReactRenderer<unknown, DotPhraseSuggestionListProps>;
type PopupPlacement = "above" | "below";
type PlacementCause = "typing" | "viewport";

/**
 * Reads the height of the visible viewport, excluding any horizontal scrollbar. A `position: fixed`
 * element's `bottom` offset is measured against this same height.
 * @returns The viewport height in CSS pixels.
 */
function getViewportHeight(): number {
  return document.documentElement.clientHeight;
}

/**
 * Picks the side of the caret for the current list height: below when it fits, otherwise the side with more room.
 * @param popup - The mounted popup element, measured for its height.
 * @param rect - The caret rect.
 * @returns The side the popup should sit on for the height it was measured at.
 */
export function choosePopupPlacement(popup: HTMLElement, rect: DOMRect): PopupPlacement {
  const { height } = popup.getBoundingClientRect();
  const spaceBelow = getViewportHeight() - rect.bottom - POPUP_GAP;
  const spaceAbove = rect.top - POPUP_GAP;

  if (height <= spaceBelow || spaceBelow >= spaceAbove) return "below";

  return "above";
}

/**
 * Pins the popup to the caret. Above the caret it is anchored by its bottom edge, so it stays against
 * the caret while the list grows or shrinks without needing a fresh height measurement.
 * @param popup - The popup element to move.
 * @param rect - The caret rect; nothing moves when it is missing.
 * @param placement - The side of the caret the popup sits on.
 */
export function positionPopup(
  popup: HTMLElement,
  rect: DOMRect | null | undefined,
  placement: PopupPlacement,
): void {
  if (!rect) return;

  popup.style.left = `${rect.left}px`;

  if (placement === "above") {
    const viewportHeight = getViewportHeight();
    const { height } = popup.getBoundingClientRect();

    // Capped so a popup taller than the space above keeps its top edge inside the window.
    popup.style.top = "";
    popup.style.bottom = `${Math.min(viewportHeight - rect.top + POPUP_GAP, viewportHeight - height)}px`;

    return;
  }

  popup.style.bottom = "";
  popup.style.top = `${rect.bottom + POPUP_GAP}px`;
}

export function buildDotPhraseSuggestionRender(): SuggestionRender {
  return () => {
    let renderer: PhraseRenderer | undefined;
    let props: SuggestionProps<InsertableDotPhrase> | undefined;
    let placement: PopupPlacement = "below";
    let settleFrame: number | undefined;
    let selectedIndex = 0;

    const dotPhrases = () => props?.items ?? [];

    const listProps = (): DotPhraseSuggestionListProps => ({
      dotPhrases: dotPhrases(),
      selectedIndex,
      onSelect: (dotPhrase) => props?.command(dotPhrase),
      onHighlight: highlight,
    });

    function highlight(index: number) {
      selectedIndex = index;
      renderer?.updateProps(listProps());
    }

    function untrackViewport() {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    }

    const destroy = () => {
      untrackViewport();
      cancelSettle();
      renderer?.element.remove();
      renderer?.destroy();
      renderer = undefined;
      placement = "below";
    };

    function isTriggerVisible(rect: DOMRect) {
      return rect.bottom > 0 && rect.top < getViewportHeight();
    }

    /**
     * Pins the popup to the caret, closing it when a viewport change moves the caret out of view.
     * @param cause - What moved the caret or the list: a keystroke, or a scroll or resize.
     */
    function place(cause: PlacementCause) {
      if (!renderer) return;

      const rect = props?.clientRect?.();

      if (!rect) return;

      if (cause === "viewport" && !isTriggerVisible(rect)) {
        destroy();

        return;
      }

      // Scroll and resize leave the list's height as measured, so either side can be picked again.
      // While typing, a popup below may flip above, but a popup above stays above until it closes.
      if (cause === "viewport" || placement === "below") {
        placement = choosePopupPlacement(renderer.element, rect);
      }

      positionPopup(renderer.element, rect, placement);
    }

    function reposition() {
      place("viewport");
    }

    function trackViewport() {
      window.addEventListener("scroll", reposition, true);
      window.addEventListener("resize", reposition);
    }

    function cancelSettle() {
      if (settleFrame !== undefined) cancelAnimationFrame(settleFrame);

      settleFrame = undefined;
    }

    // React commits the new list after updateProps, so the height is measured again on the next frame.
    function settleAfterCommit() {
      cancelSettle();
      settleFrame = requestAnimationFrame(() => {
        settleFrame = undefined;
        place("typing");
      });
    }

    const move = (offset: number) => {
      const count = dotPhrases().length;

      if (count > 0) highlight((selectedIndex + offset + count) % count);
    };

    const selectHighlighted = () => {
      const dotPhrase = dotPhrases()[selectedIndex];

      if (dotPhrase) props?.command(dotPhrase);
    };

    const KEY_ACTIONS: Record<string, () => void> = {
      ArrowDown: () => move(1),
      ArrowUp: () => move(-1),
      Enter: selectHighlighted,
      Tab: selectHighlighted,
      Escape: destroy,
    };

    const render = (nextProps: SuggestionProps<InsertableDotPhrase>) => {
      props = nextProps;

      if (dotPhrases().length === 0) {
        destroy();

        return;
      }

      if (selectedIndex >= dotPhrases().length) selectedIndex = 0;

      if (!renderer) {
        renderer = new ReactRenderer(DotPhraseSuggestionList, {
          editor: nextProps.editor,
          props: listProps(),
          className: classes.popupLayer,
        });
        document.body.appendChild(renderer.element);
        trackViewport();
      }

      renderer.updateProps(listProps());
      place("typing");
      settleAfterCommit();
    };

    return {
      onStart(startProps) {
        selectedIndex = 0;
        render(startProps);
      },

      onUpdate(updateProps) {
        render(updateProps);
      },

      onKeyDown({ event }) {
        // Shift combinations stay with the editor, so Shift+Enter still breaks the line.
        if (!renderer || event.shiftKey) return false;

        const action = KEY_ACTIONS[event.key];

        if (!action) return false;

        action();

        return true;
      },

      onExit() {
        destroy();
      },
    };
  };
}
